import { SxProps, Theme } from '@mui/material/styles';
import { colors } from './index';

// Common styling patterns
export const commonStyles = {
  // Card styles
  card: {
    background: colors.background.paper,
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.border.main}`,
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      transform: 'translateY(-2px)',
      transition: 'all 0.2s ease',
    },
  } as SxProps<Theme>,

  // Section header styles
  sectionHeader: {
    background: colors.primary.main,
    color: colors.primary.contrastText,
    borderRadius: '8px 8px 0 0',
    padding: '16px 24px',
    fontWeight: 600,
  } as SxProps<Theme>,

  // Status chip styles
  statusChip: {
    present: {
      backgroundColor: colors.status.present,
      color: colors.status.present,
      '& .MuiChip-label': {
        color: 'white',
      },
    },
    absent: {
      backgroundColor: colors.status.absent,
      color: colors.status.absent,
      '& .MuiChip-label': {
        color: 'white',
      },
    },
    pending: {
      backgroundColor: colors.status.pending,
      color: colors.status.pending,
      '& .MuiChip-label': {
        color: 'white',
      },
    },
    approved: {
      backgroundColor: colors.status.approved,
      color: colors.status.approved,
      '& .MuiChip-label': {
        color: 'white',
      },
    },
    rejected: {
      backgroundColor: colors.status.rejected,
      color: colors.status.rejected,
      '& .MuiChip-label': {
        color: 'white',
      },
    },
  } as Record<string, SxProps<Theme>>,

  // Button styles
  button: {
    primary: {
      background: colors.primary.main,
      color: colors.primary.contrastText,
      '&:hover': {
        background: colors.primary.dark,
      },
    },
    secondary: {
      background: colors.secondary.main,
      color: colors.secondary.contrastText,
      '&:hover': {
        background: colors.secondary.dark,
      },
    },
    success: {
      background: colors.success.main,
      color: colors.success.contrastText,
      '&:hover': {
        background: colors.success.dark,
      },
    },
    warning: {
      background: colors.warning.main,
      color: colors.warning.contrastText,
      '&:hover': {
        background: colors.warning.dark,
      },
    },
  } as Record<string, SxProps<Theme>>,

  // Layout styles
  layout: {
    pageContainer: {
      minHeight: '100vh',
      background: colors.background.default,
      padding: 3,
    },
    contentContainer: {
      maxWidth: 'xl',
      padding: 2,
    },
    section: {
      marginBottom: 3,
    },
  } as Record<string, SxProps<Theme>>,

  // Form styles
  form: {
    field: {
      marginBottom: 2,
    },
    section: {
      padding: 3,
      background: colors.background.paper,
      borderRadius: 2,
      border: `1px solid ${colors.border.main}`,
      marginBottom: 2,
    },
  } as Record<string, SxProps<Theme>>,

  // Table styles
  table: {
    container: {
      background: colors.background.paper,
      borderRadius: 2,
      border: `1px solid ${colors.border.main}`,
      overflow: 'hidden',
    },
    header: {
      background: colors.primary.main,
      color: colors.primary.contrastText,
    },
    row: {
      '&:hover': {
        background: colors.background.accent,
      },
    },
  } as Record<string, SxProps<Theme>>,

  // Loading styles
  loading: {
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(255,255,255,0.9)',
      backdropFilter: 'blur(5px)',
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 3,
      borderRadius: 2,
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 4,
      borderRadius: 2,
      background: 'rgba(255,255,255,0.8)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    },
  } as Record<string, SxProps<Theme>>,

  // Empty state styles
  emptyState: {
    container: {
      textAlign: 'center',
      padding: 8,
      color: colors.text.secondary,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
    },
    icon: {
      padding: 3,
      borderRadius: '50%',
      background: colors.background.accent,
      marginBottom: 2,
      border: `1px solid ${colors.border.main}`,
    },
  } as Record<string, SxProps<Theme>>,
};

// Utility functions
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    present: colors.status.present,
    absent: colors.status.absent,
    pending: colors.status.pending,
    approved: colors.status.approved,
    rejected: colors.status.rejected,
  };
  return statusColors[status] || colors.text.secondary;
};

export const getStatusChipStyle = (status: string): SxProps<Theme> => {
  return commonStyles.statusChip[status] || commonStyles.statusChip.pending;
};

export const getButtonStyle = (variant: string): SxProps<Theme> => {
  return commonStyles.button[variant] || commonStyles.button.primary;
};

// Responsive breakpoints
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// Spacing utilities
export const spacing = {
  xs: 0.5,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
};

// Export colors for direct use
export { colors }; 